import type { DisplayPoint, ModelElement } from './types';
// ============================================================================
// CONSTANTS
// ============================================================================

export const TELEMETRY_COLL_USER_TYPE = 'iaf_telemetry_timeseries';
export const DISPLAY_POINTS_USER_TYPE = 'telemetry_display_pts';

export const ASSET_TYPES = {
  HYDRONIC_CHILLER: 'Hydronic Chiller',
  AHU: 'AHU',
} as const;

export type AssetTypeKey = keyof typeof ASSET_TYPES;
export type AssetTypeValue = typeof ASSET_TYPES[AssetTypeKey];

// ============================================================================
// MODEL ELEMENTS (Hardcoded for now - replace with API fetch later)
// ============================================================================

export const MODEL_ELEMENTS: ModelElement[] = [
  {
    source_id: '3add4d65-8a40-4fb2-90c6-2fb57eb23b33-001adeb2',
    properties: {
      IfcGUID: { val: '0wtKrbYa1Fif36BxL_gEM1' },
      'Asset Type': { val: 'Hydronic Chiller' },
      'BA Name': { val: 'Liebert DSE DA85' },
    },
  },
  {
    source_id: 'd4beca36-4e65-4457-a855-2d1ac8a51205-00195657',
    properties: {
      IfcGUID: { val: '3KliesJcL4LwXLBHh8l4HI' },
      'Asset Type': { val: 'AHU' },
      'BA Name': { val: 'Liebert DSE DA85' },
    },
  },
];

// ============================================================================
// DISPLAY POINT DESCRIPTIONS
// ============================================================================

export const DISPLAY_POINT_DESCRIPTIONS: Record<string, string> = {
  HC_Energy_kW: 'Hydronic chiller power draw (compressor + auxiliaries) in kW.',
  HC_LWT_F: 'Hydronic chiller leaving water temperature (LWT) in °F.',
  HC_EWT_F: 'Hydronic chiller entering water temperature (EWT) in °F.',
  HC_LoopFlow_GPM: 'Hydronic chiller loop water flow rate in GPM.',
  HC_LoopSideEWT_F: 'Loop side entering water temperature in °F.',
  HC_LoopSideLWT_F: 'Loop side leaving water temperature in °F.',
  HC_CompRuntimeHours: 'Total compressor runtime hours (cumulative).',
  HC_CompStartsDaily: 'Number of compressor starts per day.',
  AHU_Energy_kW: 'AHU power draw (fans, heaters, etc.) in kW.',
  AHU_SupplyAirTemp_F: 'AHU supply/output air temperature in °F.',
  AHU_ReturnAirTemp_F: 'AHU return air temperature in °F.',
  AHU_SupplyAirRH_pct: 'AHU supply/output air relative humidity (%RH).',
  AHU_ProcessAirflow_CFM: 'AHU process airflow in CFM.',
  AHU_ProcessFanRPM: 'AHU process fan speed in RPM.',
  AHU_ReactivationFanRPM: 'AHU reactivation fan speed in RPM.',
  AHU_Water_GPH: 'AHU water consumption in gallons per hour (GPH).',
};

// ============================================================================
// DEFAULT DISPLAY POINTS
// ============================================================================

export const DEFAULT_DISPLAY_POINTS: DisplayPoint[] = [
  // =====================================
  // HYDRONIC CHILLER POINTS
  // =====================================
  {
    pointName: 'HC_Energy_kW',
    kind: 'Number',
    displayName: 'Energy Consumption',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_Energy_kW,
    iconClass: 'fa-solid fa-bolt',
    format: '',
    unit: 'kW',
    aggregation: 'avg',
    places: 2,
    ranges: [
      { name: 'Below expected', from: Number.MIN_SAFE_INTEGER, to: 8.5, color: '#ef5350', feeling: -1 },
      { name: 'Normal', from: 8.5, to: 10, color: '#8bc34a', feeling: 1 },
      { name: 'Above expected', from: 10, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'elec-power',
  },
  {
    pointName: 'HC_LWT_F',
    kind: 'Number',
    displayName: 'Leaving Water Temp (LWT)',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_LWT_F,
    iconClass: 'fa-solid fa-temperature-half',
    format: '',
    unit: '°F',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Too cold', from: Number.MIN_SAFE_INTEGER, to: 42, color: '#1d88c9', feeling: -1 },
      { name: 'In range', from: 42, to: 60, color: '#8bc34a', feeling: 1 },
      { name: 'Too warm', from: 60, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'chilled-water-leaving-temp',
  },
  {
    pointName: 'HC_EWT_F',
    kind: 'Number',
    displayName: 'Entering Water Temp (EWT)',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_EWT_F,
    iconClass: 'fa-solid fa-temperature-half',
    format: '',
    unit: '°F',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Below spec', from: Number.MIN_SAFE_INTEGER, to: 60, color: '#1d88c9', feeling: -1 },
      { name: 'In range', from: 60, to: 95, color: '#8bc34a', feeling: 1 },
      { name: 'Above spec', from: 95, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'chilled-water-entering-temp',
  },
  {
    pointName: 'HC_LoopFlow_GPM',
    kind: 'Number',
    displayName: 'Loop Water Flow Rate',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_LoopFlow_GPM,
    iconClass: 'fa-solid fa-water',
    format: '',
    unit: 'GPM',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Too low', from: Number.MIN_SAFE_INTEGER, to: 20, color: '#ef5350', feeling: -1 },
      { name: 'In range', from: 20, to: 36, color: '#8bc34a', feeling: 1 },
      { name: 'Too high', from: 36, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'chilled-water-flow',
  },
  {
    pointName: 'HC_LoopSideEWT_F',
    kind: 'Number',
    displayName: 'Loop Side Entering Water Temp',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_LoopSideEWT_F,
    iconClass: 'fa-solid fa-temperature-half',
    format: '',
    unit: '°F',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Below spec', from: Number.MIN_SAFE_INTEGER, to: 75, color: '#1d88c9', feeling: -1 },
      { name: 'In range', from: 75, to: 85, color: '#8bc34a', feeling: 1 },
      { name: 'Above spec', from: 85, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'condenser-water-entering-temp',
  },
  {
    pointName: 'HC_LoopSideLWT_F',
    kind: 'Number',
    displayName: 'Loop Side Leaving Water Temp',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_LoopSideLWT_F,
    iconClass: 'fa-solid fa-temperature-half',
    format: '',
    unit: '°F',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Below spec', from: Number.MIN_SAFE_INTEGER, to: 104, color: '#1d88c9', feeling: -1 },
      { name: 'In range', from: 104, to: 118, color: '#8bc34a', feeling: 1 },
      { name: 'Above spec', from: 118, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'condenser-water-leaving-temp',
  },
  {
    pointName: 'HC_CompRuntimeHours',
    kind: 'Number',
    displayName: 'Compressor Runtime Total Hours',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_CompRuntimeHours,
    iconClass: 'fa-regular fa-clock',
    format: '',
    unit: 'h',
    aggregation: 'max',
    places: 0,
    ranges: [
      { name: 'Observed', from: 0, to: Number.MAX_SAFE_INTEGER, color: '#8bc34a', feeling: 0 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'compressor-runtime',
  },
  {
    pointName: 'HC_CompStartsDaily',
    kind: 'Number',
    displayName: 'Compressor Starts (Daily)',
    description: DISPLAY_POINT_DESCRIPTIONS.HC_CompStartsDaily,
    iconClass: 'fa-solid fa-play',
    format: '',
    unit: 'count',
    aggregation: 'sum',
    places: 0,
    ranges: [
      { name: 'Low cycling', from: Number.MIN_SAFE_INTEGER, to: 8, color: '#1d88c9', feeling: -1 },
      { name: 'Normal', from: 8, to: 16, color: '#8bc34a', feeling: 1 },
      { name: 'High cycling', from: 16, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: 'compressor-starts',
  },
  // =====================================
  // AHU POINTS
  // =====================================
  {
    pointName: 'AHU_Energy_kW',
    kind: 'Number',
    displayName: 'Energy Consumption',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_Energy_kW,
    iconClass: 'fa-solid fa-bolt',
    format: '',
    unit: 'kW',
    aggregation: 'avg',
    places: 2,
    ranges: [
      { name: 'Below expected', from: Number.MIN_SAFE_INTEGER, to: 15, color: '#ef5350', feeling: -1 },
      { name: 'Normal', from: 15, to: 25, color: '#8bc34a', feeling: 1 },
      { name: 'Above expected', from: 25, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'elec-power',
  },
  {
    pointName: 'AHU_SupplyAirTemp_F',
    kind: 'Number',
    displayName: 'Output Air Temperature',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_SupplyAirTemp_F,
    iconClass: 'fa-solid fa-temperature-half',
    format: '',
    unit: '°F',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Below spec', from: Number.MIN_SAFE_INTEGER, to: 82, color: '#1d88c9', feeling: -1 },
      { name: 'In range', from: 82, to: 99, color: '#8bc34a', feeling: 1 },
      { name: 'Above spec', from: 99, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'discharge-air-temp',
  },
  {
    pointName: 'AHU_ReturnAirTemp_F',
    kind: 'Number',
    displayName: 'Return Air Temperature',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_ReturnAirTemp_F,
    iconClass: 'fa-solid fa-temperature-half',
    format: '',
    unit: '°F',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Below spec', from: Number.MIN_SAFE_INTEGER, to: 64, color: '#1d88c9', feeling: -1 },
      { name: 'In range', from: 64, to: 72, color: '#8bc34a', feeling: 1 },
      { name: 'Above spec', from: 72, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'return-air-temp',
  },
  {
    pointName: 'AHU_SupplyAirRH_pct',
    kind: 'Number',
    displayName: 'Output Air Humidity',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_SupplyAirRH_pct,
    iconClass: 'fa-solid fa-droplet',
    format: '',
    unit: '%RH',
    aggregation: 'avg',
    places: 1,
    ranges: [
      { name: 'Too dry', from: Number.MIN_SAFE_INTEGER, to: 40, color: '#1d88c9', feeling: -1 },
      { name: 'Comfort', from: 40, to: 60, color: '#8bc34a', feeling: 1 },
      { name: 'Too humid', from: 60, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'discharge-air-humidity',
  },
  {
    pointName: 'AHU_ProcessAirflow_CFM',
    kind: 'Number',
    displayName: 'Process Airflow',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_ProcessAirflow_CFM,
    iconClass: 'fa-solid fa-wind',
    format: '',
    unit: 'CFM',
    aggregation: 'avg',
    places: 0,
    ranges: [
      { name: 'Low', from: Number.MIN_SAFE_INTEGER, to: 12355, color: '#ef5350', feeling: -1 },
      { name: 'Normal', from: 12355, to: 17650, color: '#8bc34a', feeling: 1 },
      { name: 'High', from: 17650, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'discharge-air-flow',
  },
  {
    pointName: 'AHU_ProcessFanRPM',
    kind: 'Number',
    displayName: 'Process Fan RPM',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_ProcessFanRPM,
    iconClass: 'fa-solid fa-fan',
    format: '',
    unit: 'RPM',
    aggregation: 'avg',
    places: 0,
    ranges: [
      { name: 'Too low', from: Number.MIN_SAFE_INTEGER, to: 900, color: '#ef5350', feeling: -1 },
      { name: 'In range', from: 900, to: 1450, color: '#8bc34a', feeling: 1 },
      { name: 'Too high', from: 1450, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'supply-fan-speed',
  },
  {
    pointName: 'AHU_ReactivationFanRPM',
    kind: 'Number',
    displayName: 'Reactivation Fan RPM',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_ReactivationFanRPM,
    iconClass: 'fa-solid fa-fan',
    format: '',
    unit: 'RPM',
    aggregation: 'avg',
    places: 0,
    ranges: [
      { name: 'Too low', from: Number.MIN_SAFE_INTEGER, to: 600, color: '#ef5350', feeling: -1 },
      { name: 'In range', from: 600, to: 1000, color: '#8bc34a', feeling: 1 },
      { name: 'Too high', from: 1000, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'return-fan-speed',
  },
  {
    pointName: 'AHU_Water_GPH',
    kind: 'Number',
    displayName: 'Water Consumption',
    description: DISPLAY_POINT_DESCRIPTIONS.AHU_Water_GPH,
    iconClass: 'fa-solid fa-droplet',
    format: '',
    unit: 'GPH',
    aggregation: 'sum',
    places: 1,
    ranges: [
      { name: 'Below expected', from: Number.MIN_SAFE_INTEGER, to: 100, color: '#1d88c9', feeling: -1 },
      { name: 'Normal', from: 100, to: 160, color: '#8bc34a', feeling: 1 },
      { name: 'Above expected', from: 160, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.AHU],
    haystackTag: 'water-flow',
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getCollectionSettings(
  assetType: string,
  aggregation: 'raw' | 'minutes' | 'hourly',
  granularity?: 'seconds' | 'minutes' | 'hours',
  expiry?: number
) {
  // If custom values provided, use them
  if (granularity && expiry !== undefined) {
    return {
      _tsProperties: {
        _granularity: granularity,
        _expiry: expiry,
      },
    };
  }

  // Otherwise use defaults based on aggregation type
  if (aggregation === 'hourly') {
    return {
      _tsProperties: {
        _granularity: 'hours',
        _expiry: 60 * 60 * 24 * 30, // 30 days
      },
    };
  }
  if (aggregation === 'minutes') {
    return {
      _tsProperties: {
        _granularity: 'minutes',
        _expiry: 60 * 60 * 24 * 7, // 7 days
      },
    };
  }
  // raw
  return {
    _tsProperties: {
      _granularity: 'seconds',
      _expiry: 60 * 60 * 24, // 24 hours
    },
  };
}

export function getSourceIdFromElement(element: ModelElement): string {
  return (
    element.source_id ||
    element.properties?.IfcGUID?.val ||
    ''
  );
}

export function getEmptyDisplayPointForm(): import('./types').DisplayPoint {
  return {
    pointName: '',
    kind: 'Number',
    displayName: '',
    description: '',
    iconClass: 'fa-solid fa-gauge',
    format: '',
    unit: '',
    aggregation: 'avg',
    places: 2,
    ranges: [
      { name: 'Low', from: Number.MIN_SAFE_INTEGER, to: 0, color: '#ef5350', feeling: -1 },
      { name: 'Normal', from: 0, to: 100, color: '#8bc34a', feeling: 1 },
      { name: 'High', from: 100, to: Number.MAX_SAFE_INTEGER, color: '#ef5350', feeling: -1 },
    ],
    dtTypes: [ASSET_TYPES.HYDRONIC_CHILLER],
    haystackTag: ''
  };
}




